import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, CheckCircle, Download, Search, Calendar, CreditCard, Banknote } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FineCollectionReport = () => {
  const [filters, setFilters] = useState({ 
    from_date: '', 
    to_date: '',
    payment_mode: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const fetchReport = async () => {
    if (!filters.from_date || !filters.to_date) {
      alert('Please select both From Date and To Date');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.payment_mode) params.append('payment_mode', filters.payment_mode);

      const response = await axios.get(`${API_URL}/api/attendance/fine-collection-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Error fetching collection report data');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData || !reportData.collections) return;

    const data = filteredCollections.map(item => ({
      'Receipt No': item.receipt_number,
      'Date': item.payment_date,
      'Admission No': item.admission_number,
      'Student Name': item.student_name,
      'Amount': item.amount,
      'Payment Mode': item.payment_mode,
      'Transaction Details': item.transaction_details || 'N/A',
      'Collected By': item.collected_by
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fine Collections');
    XLSX.writeFile(wb, `Fine_Collection_Report_${filters.from_date}_to_${filters.to_date}.xlsx`);
  };

  const filteredCollections = reportData?.collections?.filter(item => {
    const matchesSearch = item.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  const stats = reportData?.statistics || {
    total_collected: 0,
    pending_collection: 0,
    collection_count: 0,
    collection_rate: 0,
    cash_collections: 0,
    online_collections: 0
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fine Collection Report</h1>
          <p className="text-sm text-gray-500 mt-1">Track collected attendance fines</p>
        </div>
        {reportData && (
          <button 
            onClick={exportToExcel}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export to Excel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-100">Total Collected</p>
              <p className="text-3xl font-bold mt-1">₹{stats.total_collected}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-100">Pending Collection</p>
              <p className="text-3xl font-bold mt-1">₹{stats.pending_collection}</p>
            </div>
            <DollarSign className="h-12 w-12 text-orange-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100">Collection Count</p>
              <p className="text-3xl font-bold mt-1">{stats.collection_count}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-100">Collection Rate</p>
              <p className="text-3xl font-bold mt-1">{stats.collection_rate}%</p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-3 mb-3">
            <Banknote className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Cash Collections</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.cash_collections}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-3 mb-3">
            <CreditCard className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Online Collections</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.online_collections}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date *</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date *</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.payment_mode}
              onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value })}
            >
              <option value="">All Modes</option>
              <option value="Cash">Cash</option>
              <option value="Online">Online</option>
              <option value="UPI">UPI</option>
              <option value="Card">Card</option>
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={fetchReport}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex gap-4 items-center mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, admission number, or receipt number..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction Details</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collected By</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCollections.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-blue-600">{item.receipt_number}</td>
                      <td className="px-4 py-3 text-sm">{item.payment_date}</td>
                      <td className="px-4 py-3 text-sm">{item.admission_number}</td>
                      <td className="px-4 py-3 text-sm font-medium">{item.student_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">₹{item.amount}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          item.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {item.payment_mode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.transaction_details || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.collected_by}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => window.open(`${API_URL}/api/attendance/fine-receipt/${item.receipt_number}?token=${token}`, '_blank')}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          title="Print Receipt"
                        >
                          <Download className="h-4 w-4" />
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredCollections.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No collection data available for the selected filters
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!reportData && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select date range and click "Generate Report" to view collection data</p>
        </div>
      )}
    </div>
  );
};

export default FineCollectionReport;